/* eslint-disable */
// @ts-nocheck
// TODO fix type checking for this file

import type { ResumeTheme } from '../common/base-theme';

type Args = {
  srcId: string;
  destinationId: string;
  resumeTheme: ResumeTheme;
};

// This function takes a web resume and breaks it into pages for a PDF
const pageResume = ({ srcId, destinationId, resumeTheme }: Args) => {
  // Variables to track the paging status
  const pages: Array<HTMLElement> = [];
  let currentPage: HTMLElement;
  let currentPageIndex = 0;
  let currentColumn: HTMLElement;
  let currentColumnHeight = 0;
  let currentColumnIndex = 0;
  // Page size and spacing
  const { paddingX, paddingY } = resumeTheme.print;
  const pageHeight = 1332;
  const pageWidth = 1030;
  const contentHeight = pageHeight - paddingY * 2;
  const contentWidth = pageWidth - paddingX * 2;
  // Get layout elements
  const srcEl = document.querySelector(`#${srcId}`);
  const destinationEl = document.querySelector(`#${destinationId}`);

  // Assure the elements exist
  if (!srcEl) {
    throw new Error(`Unable to find element for srcId: ${srcId}`);
  }

  if (!destinationEl) {
    throw new Error(
      `Unable to find element for destinationEl: ${destinationId}`
    );
  }

  // Classes used by templates to indicate resume structure
  const pageContainerClass = 'pdf-page-container';
  const pageColumnClass = 'pdf-page-column';
  const resumeHeaderClass = 'pdf-resume-header';
  // Get the page structure components
  const pageContainer = srcEl.querySelector(`.${pageContainerClass}`);
  const columnContainers = srcEl.querySelectorAll(`.${pageColumnClass}`);
  const resumeHeader = srcEl.querySelector(`.${resumeHeaderClass}`);

  // Assure the page container element exists
  if (!pageContainer) {
    throw new Error(
      `Unable to find pageContainer with class ${pageContainerClass}`
    );
  }

  // Assure that there is at least one column container
  if (!columnContainers || columnContainers.length === 0) {
    throw new Error(
      `Unable to find at least one columnContainer with class ${pageColumnClass}`
    );
  }

  // Assure the resumeHeader exists
  if (!resumeHeader) {
    throw new Error(
      `Unable to find resumeHeader with class ${resumeHeaderClass}`
    );
  }

  // Add a style tags with the required css
  addStyles();
  // Prepare the src and destination elements for paging
  prepareForPaging();
  // Make the first (empty) page
  createPage({
    destinationEl,
  });

  for (const columnContainer of columnContainers) {
    for (const element of columnContainer.children) {
      addElementToColumn(element);
    }

    // Update the paging status variables
    currentColumnIndex = currentColumnIndex + 1;
    currentColumn = pages[0].querySelectorAll(`.${pageColumnClass}`)[
      currentColumnIndex
    ];
    const headerHeight = getHeightWithMargins(resumeHeader);
    currentColumnHeight = headerHeight.withBothMargins;
    currentPageIndex = 0;
    currentPage = pages[0];
  }

  // Hide the srcEl
  srcEl.style.display = 'none';

  // Adds the element to the current column following the layout logic
  function addElementToColumn(element: HTMLElement) {
    if (fitsInColumn(element)) {
      // Add it to the column if it fits
      appendElementToCurrentColumn(element);
    } else if (avoidPageBreakInside(element)) {
      // If this is the last page in pages, create a new page
      if (currentPageIndex === pages.length - 1) {
        // Create a new page and add element
        createPage({
          destinationEl,
          initialElement: element,
        });
      } else {
        // Update paging status vars to next page and column
        currentPageIndex = currentPageIndex + 1;
        currentPage = pages[currentPageIndex];
        currentColumn = currentPage.querySelectorAll(`.${pageColumnClass}`)[
          currentColumnIndex
        ];
        currentColumnHeight = 0;
        appendElementToCurrentColumn(element);
      }
    } else if (element.children.length > 0) {
      // Recurse trying to add children to page
      for (const childElement of element.children) {
        addElementToColumn(childElement);
      }
    } else {
      // If this is the last page in pages, create a new page
      if (currentPageIndex === pages.length - 1) {
        // Create a new page and add element
        createPage({
          destinationEl,
          initialElement: element,
        });
      } else {
        // Update paging status vars to next page and column
        currentPageIndex = currentPageIndex + 1;
        currentPage = pages[currentPageIndex];
        currentColumn = currentPage.querySelectorAll(`.${pageColumnClass}`)[
          currentColumnIndex
        ];
        currentColumnHeight = 0;
        appendElementToCurrentColumn(element);
      }
    }
  }

  function createPage({
    destinationEl,
    initialElement,
  }: {
    destinationEl: HTMLElement;
    initialElement?: HTMLElement;
  }) {
    // Create the outer container for the new page
    const newPage = document.createElement('div');
    newPage.className = `pdf-page pdf-page-${currentPageIndex + 1}`;
    // Update paging status variables
    pages.push(newPage);
    currentPage = newPage;
    currentColumnHeight = 0;

    // Add the full width resume header before starting columns if first page
    if (pages.length === 1 && resumeHeader) {
      newPage.appendChild(resumeHeader.cloneNode(true));
      const height = getHeightWithMargins(resumeHeader);
      currentColumnHeight = height.withBothMargins;
    }

    // Add the columns and their container to the page
    const pageContainerClone = pageContainer.cloneNode(false);

    for (let i = 0; i < columnContainers.length; i++) {
      const columnContainer = columnContainers[i].cloneNode(false);
      pageContainerClone.appendChild(columnContainer);

      // Update the currentColumn to point to the new page
      if (i === currentColumnIndex) {
        currentColumn = columnContainer;
      }
    }

    newPage.appendChild(pageContainerClone);

    // Add the initialElement to the currentColumn if it exists
    if (initialElement) {
      appendElementToCurrentColumn(initialElement);
    }

    // Don't increase the index if this is the first page added because the
    // index should be 0 (pointing to this page).
    if (pages.length !== 1) {
      currentPageIndex = currentPageIndex + 1;
    }

    destinationEl.appendChild(newPage);
  }

  // Checks if an element fits on the current page in the current column
  function fitsInColumn(element: HTMLElement): boolean {
    const height = getHeightWithMargins(element);
    // Don't worry about bottom margin when checking if it fits on the page because
    // it's ok if the bottom margin gets clipped by the end of the page.
    return height.withOnlyTopMargin + currentColumnHeight < contentHeight;
  }

  // Appends an element to the current page
  function appendElementToCurrentColumn(element: HTMLElement) {
    const height = getHeightWithMargins(element);
    currentColumnHeight = currentColumnHeight + height.withBothMargins;
    currentColumn.appendChild(element.cloneNode(true));
  }

  // Prepare the src and destination elements for paging
  function prepareForPaging() {
    // Clear the destinationEl before starting. This will contain the last output
    // of this function if this isn't the first run.
    destinationEl.innerHTML = '';
    // Assure the srcEl isn't hidden. If this isn't the first render, it would
    // have been hidden after using the src to render the pages in destinationEl.
    srcEl.style.display = 'block';
    // Need to make sure that the source element has the same width as the
    // destination or the line wraps can cause different hieghts.
    srcEl.style.width = `${contentWidth}px`;
    destinationEl.style.width = `${pageWidth}px`;
  }

  // Styles required by PDF layout components
  function addStyles() {
    const head = document.head;
    const style = document.createElement('style');
    const css = `
      @page: { size: 'letter'; }
      .pdf-page {
        box-sizing: border-box;
        width: ${pageWidth}px;
        height: ${pageHeight}px;
        padding: ${paddingY}px ${paddingX}px;
        background-color: ${resumeTheme.print.backgroundColor};
        overflow: hidden;

        /* Force Chrome to use the exact colors. It will try and change them to
         * optimize for printing without this. Need flowfixme to avoid error. */
        -webkit-print-color-adjust: exact;

        /* TODO leave this off while testing to catch errors, but turn on
         * before releasing as it will make errors less noticeable. */
        /* page-break-after: always; */
      }

    `;

    if (head) {
      head.appendChild(style);
      style.type = 'text/css';
      style.appendChild(document.createTextNode(css));
    }
  }
};

function avoidPageBreakInside(element: HTMLElement): boolean {
  const s = window.getComputedStyle(element, null);
  return s.getPropertyValue('page-break-inside') === 'avoid';
}

// This is needed because offsetHeight and clientHeight don't include margins
function getHeightWithMargins(el: HTMLElement): {
  withBothMargins: number;
  withOnlyTopMargin: number;
} {
  const s = window.getComputedStyle(el, null);
  return {
    withBothMargins:
      el.offsetHeight +
      parseInt(s.getPropertyValue('margin-top')) +
      parseInt(s.getPropertyValue('margin-bottom')),
    withOnlyTopMargin:
      el.offsetHeight + parseInt(s.getPropertyValue('margin-top')),
  };
}

export default pageResume;
